import { useNavigation } from '@react-navigation/native';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { useSelector } from 'react-redux';

import actions, { back } from '../../actions';
import {
    addProviderError,
    batchChangeConsent
} from '../../actions/consents';
import { getConsentData } from '../../agreementsConfig';
import {
    Button,
    ButtonsGroup,
    Page,
    Platform,
    Title,
    TopNotification,
    View
} from '../../common';
import { cleanString } from '../../common/Agreemants';
import { HyperLink } from '../../common/Hyperlink.js';
import { SHOW_CONSENTS_AS_TEXT } from '../../config.js';
import {
    usePersonalData,
    USER_AGREEMENT_KEY,
    USER_CLINIC_HISTORY_KEY,
    USER_HISTORY_KEY
} from '../../hooks/usePersonalData';
import { getProviders } from '../../selectors/consents';
import { getProviderCapabilities } from '../../selectors/providerCodes';
import { openURL } from '../../services/device';
import { getHasLocalVisits } from '../../services/user.utils';
import { buttonsStyles } from '../../styles/button.js';
import { capitalize } from '../../utils';
import {
    CLINIC_CONSENT_KEYS,
    getClinicsConsents,
    initializeClinicsConsentsState,
    shouldShowConsentCheckbox
} from '../../utils/consentsUtils';
import { useBackHandler } from '../../utils/routing';
import { getSupportBody } from '../../utils/supportInfo';
import { StyledAgreementCheckbox } from '../Visits/snippets/NewVisitModal/snippets.js';
import { ClinicConsentsGroup } from './ClinicConsentsGroup';
import {
    ConsentRetractionModal,
    shouldShowConsentRetractionModal
} from './ConsentRetractionModal';

export const MainUserConsent = ({ onConsentUpdateComplete }) => {
    const {
        consents,
        dispatch,
        userInfo,
        isLoading,
    } = usePersonalData();

    const providers = useSelector(getProviders);

    const [ error, setError ] = useState(null);
    const [ offlineClinics, setOfflineClinics ] = useState([]);

    const shouldShowUserAgreement = shouldShowConsentCheckbox(consents?.user?.main?.[USER_AGREEMENT_KEY]);
    const shouldShowMedicalDataHistory = shouldShowConsentCheckbox(consents?.user?.main?.[USER_HISTORY_KEY]);
    const policeLinkTitle = Object.R('titles.consentsPolicy');

    const [ userAgreementChecked, setUserAgreementChecked ] = useState(consents?.user?.main?.[USER_AGREEMENT_KEY] || false);
    const [ medicalDataHistoryChecked, setMedicalDataHistoryChecked ] = useState(consents?.user?.main?.[USER_HISTORY_KEY] || false);

    // Get clinic data from consents.user
    const providerCapabilities = useSelector(getProviderCapabilities);
    const clinicsConsents = getClinicsConsents(consents?.user, CLINIC_CONSENT_KEYS.USER, false, providerCapabilities);
    const [ clinicsConsentsState, setClinicsConsentsState ] = useState(
        initializeClinicsConsentsState(clinicsConsents),
    );

    // Check if at least one clinic consent should be shown
    const shouldShowClinicsSection = clinicsConsents.some(clinic => clinic.shouldShow);

    const navigation = useNavigation();
    const canLeave = useRef(false);

    // Synchronize states with consents when they change
    useEffect(() => {
        if (consents?.user?.main?.[USER_AGREEMENT_KEY] !== undefined && consents?.user?.main?.[USER_AGREEMENT_KEY] !== null) {
            setUserAgreementChecked(consents.user.main[USER_AGREEMENT_KEY]);
        }
    }, [ consents?.user?.main ]);

    useEffect(() => {
        if (consents?.user?.main?.[USER_HISTORY_KEY] !== undefined && consents?.user?.main?.[USER_HISTORY_KEY] !== null) {
            setMedicalDataHistoryChecked(consents.user.main[USER_HISTORY_KEY]);
        }
    }, [ consents?.user?.main ]);

    useEffect(() => {
        if (consents?.user) {
            const newClinicsConsents = getClinicsConsents(consents.user, CLINIC_CONSENT_KEYS.USER);
            const newClinicsState = initializeClinicsConsentsState(newClinicsConsents);

            // Merge with current state, preserving true values set by user
            setClinicsConsentsState((prevState) => {
                const mergedState = { ...newClinicsState };
                Object.keys(prevState).forEach(key => {
                    // Keep true values from previous state to prevent unwanted resets
                    if (prevState[key] === true) {
                        mergedState[key] = true;
                    }
                });
                return mergedState;
            });
        }
    }, [ consents?.user ]);

    useBackHandler(() => true);

    // Prevent "back" navigation on iOS via swipe gesture to enforce navigation only through buttons
    useEffect(() => {
        navigation.setOptions({
            gestureEnabled: false,
        });
    }, [ navigation ]);

    useEffect(() => {
        return navigation.addListener('beforeRemove', (e) => {
            if (!canLeave.current) {
                e.preventDefault();
            }
        });
    }, [ navigation ]);

    // Handler for clinic checkbox change
    const handleClinicConsentChange = (providerCode, newValue) => {
        setClinicsConsentsState((prevState) => ({
            ...prevState,
            [providerCode]: newValue,
        }));
        console.log(`Clinic consent changed: ${providerCode} = ${newValue}`);
    };

    const onSelect = () => {
        // TODO: implement onSelect for policy link
    };

    const onLogout = async () => {
        canLeave.current = true;
        Object.profilesWithoutConsent = null;
        back();
        await actions.logOut();
        if (Object.callbackOnSignIn) {
            Object.callbackOnSignIn = null;
            back();
        }
    };

    const handleConsentUpdate = useCallback(async () => {
        // Collect only consents that previously had no value (were undefined/null)
        const batchConsents = [];

        // Check USER_AGREEMENT
        if (shouldShowUserAgreement) {
            batchConsents.push({type: USER_AGREEMENT_KEY, provided: userAgreementChecked});
        }

        // Check MEDICAL_DATA_HISTORY
        if (shouldShowMedicalDataHistory) {
            batchConsents.push({type: USER_HISTORY_KEY, provided: medicalDataHistoryChecked});
        }

        // Check MEDICAL_DATA_HISTORY_CLINIC for clinics
        const clinicConsents = {};
        clinicsConsents.forEach((clinic) => {
            const providerCode = clinic?.providerCode;

            if (clinic.shouldShow) {
                if (!clinicConsents[providerCode]) {
                    clinicConsents[providerCode] = {};
                }
                batchConsents.push({type: USER_CLINIC_HISTORY_KEY, provided: clinicsConsentsState[clinic.providerCode] || false, providerCode});
                clinicConsents[providerCode][USER_CLINIC_HISTORY_KEY] = clinicsConsentsState[clinic.providerCode] || false;

                // const consentKey = `${USER_CLINIC_HISTORY_KEY}::${clinic.providerCode}`;
                // changedConsents[consentKey] = clinicsConsentsState[clinic.providerCode] || false;
            }
        });

        console.log('changedConsents: ', {
            batchConsents,
            clinicConsents,
        });

        const allClinics = Object.keys(clinicConsents);
        const offline = allClinics.filter(clinic => offlineClinics.includes(clinic));

        if (offline?.length) {
            dispatch(addProviderError(offline));
            onConsentUpdateComplete?.(userInfo?.userId);
        }

        dispatch(batchChangeConsent({profileId: userInfo?.userId, consents: batchConsents, onComplete: (result) => {
            const errors = result?.filter?.(r => r.status === 'error') || [];
            if (errors.length) {
                const errorClinics = errors.map(e => e.providerCode);
                setOfflineClinics(errorClinics);
            } else {
                setError(null);
                canLeave.current = true;
                Object.profilesWithoutConsent = null;
                onConsentUpdateComplete?.(userInfo?.userId);
            }}, onError: (e) => {
            try {
                setError({...e, level: 'error'});
            } catch {
                setError(e);
            }
        }}));
    }, [clinicsConsents,
        clinicsConsentsState,
        dispatch,
        medicalDataHistoryChecked,
        onConsentUpdateComplete,
        shouldShowMedicalDataHistory,
        shouldShowUserAgreement,
        userAgreementChecked,
        userInfo,
        offlineClinics]);


    const onConfirm = useCallback(async () => {
        const hasVisits = await getHasLocalVisits();
        // Check if modal should be shown based on consent values
        const showModalForMainProfile = shouldShowConsentRetractionModal(
            consents?.user?.main,
            USER_HISTORY_KEY,
            USER_CLINIC_HISTORY_KEY,
            clinicsConsents,
            clinicsConsentsState,
            false, // isChildProfile
            null, // storeProfileChildKey
            medicalDataHistoryChecked, // current value from UI
            false,
            hasVisits,
        );

        if (!showModalForMainProfile) {
            // If modal shouldn't be shown, proceed directly to consent update
            await handleConsentUpdate();
            return;
        }

        // Determine which blocks should be shown based on current state
        const showMedicalDataHistoryBlock = shouldShowMedicalDataHistory && !medicalDataHistoryChecked;
        const showClinicBlock = shouldShowClinicsSection &&
            clinicsConsents?.filter?.(clinic => !offlineClinics?.includes(clinic))
                .some(clinic => clinic.shouldShow && !clinicsConsentsState[clinic.providerCode]);
        const showChildStorageBlock = false; // TODO: determine based on child profile logic

        // Get unchecked clinics
        const uncheckedClinics = clinicsConsents
            .filter(clinic => clinic.shouldShow && !clinicsConsentsState[clinic.providerCode])
            .map(clinic => ({
                name: clinic.name || clinic.providerCode,
                providerCode: clinic.providerCode,
            }));

        // If any block should be shown, display confirmation modal
        if (showMedicalDataHistoryBlock || showClinicBlock || showChildStorageBlock) {
            Page.showModal(
                <ConsentRetractionModal
                    profile={{
                        name: userInfo?.name || '',
                        birthday: userInfo?.birthday || '',
                    }}
                    uncheckedClinics={uncheckedClinics}
                    showMedicalDataHistoryBlock={showMedicalDataHistoryBlock}
                    showClinicBlock={showClinicBlock}
                    showChildStorageBlock={showChildStorageBlock}
                    onConfirm={() => {
                        Page.closeModal();
                        // TODO: implement actual consent update logic
                        handleConsentUpdate();
                    }}
                    onCancel={Page.closeModal}
                />,
            );
        } else {
            // If no blocks to show, proceed with consent update directly
            handleConsentUpdate();
        }
    }, [ clinicsConsents,
        clinicsConsentsState,
        consents?.user?.main,
        handleConsentUpdate,
        medicalDataHistoryChecked,
        offlineClinics,
        shouldShowClinicsSection,
        shouldShowMedicalDataHistory,
        userInfo?.birthday,
        userInfo?.name ]);


    const clinics = useMemo(() => {
        const result = offlineClinics?.map?.(c => providers?.[c]?.name ?? c);
        return result?.join(', ') || '';
    }, [ offlineClinics, providers ]);

    const clinicError = useMemo(() => {
        return offlineClinics?.length ? {
            level: 'error',
            message: capitalize(Object.R('errors.ErrorSetClinicConsent', {
                consent: capitalize(getConsentData(USER_HISTORY_KEY)?.textWithoutPrefix ?? ''),
                clinic: clinics,
            })),
        } : '';
    }, [ offlineClinics, clinics ]);

    const onSubmitError = useCallback(() => {
        openURL(`mailto:${Object.R('supportEmail')}${getSupportBody()}`);
    }, []);

    return (
        <>
            <View style={{
                alignItems: 'center',
                marginTop: Platform.OS === 'ios' ? 60 : 40,
                marginBottom: 20,
                width: '100%',
            }}>
                <Title bold>{(Object.R('titles.usingTheApplication')).toUpperCase()}</Title>
                <Title
                    numberOfLines={null}
                    style={{
                        marginTop: 12,
                        marginBottom: shouldShowUserAgreement ? 0 : 24,
                        width: '100%',
                        color: '#9C9C9C',
                    }}
                >
                    {Object.R(shouldShowUserAgreement ? 'titles.consentsSubtitle' : 'titles.consentsSecondarySubtitle')}
                </Title>
                {shouldShowUserAgreement ? (
                    <StyledAgreementCheckbox
                        titleComponent={getConsentData(USER_AGREEMENT_KEY)?.TextData}
                        selected={userAgreementChecked}
                        onPress={() => setUserAgreementChecked(!userAgreementChecked)}
                        checkboxStyle={{
                            marginBottom: 12,
                            marginRight: 12,
                        }}
                        containerStyle={{
                            marginTop: 20,
                            marginBottom: Platform.OS === 'web' ? 6 : 16,
                        }}
                    />
                ) : null}
                {shouldShowMedicalDataHistory || shouldShowClinicsSection ? <Title
                    numberOfLines={null}
                    style={{ color: '#9C9C9C', width: '100%', }}>
                    {Object.R('titles.consentsOptionalSubtitle')}
                </Title> : null}
                {shouldShowClinicsSection ? (
                    <>
                        <Title
                            numberOfLines={null}
                            style={{ marginTop: 20, width: '100%', }}
                        >
                            {Object.R('titles.consentsClinicSubtitle')}
                        </Title>
                        <ClinicConsentsGroup
                            clinicsConsents={clinicsConsents}
                            clinicsConsentsState={clinicsConsentsState}
                            onClinicConsentChange={handleClinicConsentChange}
                            disabledClinics={offlineClinics}
                        />
                    </>
                ) : null}
                {shouldShowMedicalDataHistory ? (
                    <StyledAgreementCheckbox
                        titleComponent={getConsentData(USER_HISTORY_KEY)?.TextData}
                        selected={medicalDataHistoryChecked}
                        onPress={() => setMedicalDataHistoryChecked(!medicalDataHistoryChecked)}
                        checkboxStyle={{
                            marginBottom: 12,
                            marginRight: 12,
                        }}
                        containerStyle={{
                            marginTop: 20,
                        }}
                    />
                ) : null}
            </View>
            {(shouldShowMedicalDataHistory || shouldShowClinicsSection) && clinicError ? (<TopNotification
                hint={clinicError}
                containerStyle={{
                    borderBottomWidth: 0,
                    borderTopWidth: 1,
                    marginTop: 8,
                    marginBottom: 0,
                    paddingBottom: 0,
                }}
            />) : null}
            <View>
                <Title
                    style={{
                        marginVertical: 12,
                    }}
                >
                    <HyperLink
                        title={SHOW_CONSENTS_AS_TEXT === '1' ? cleanString(policeLinkTitle) : policeLinkTitle}
                        style={{
                            linkColor: buttonsStyles.link.color,
                            color: '#555555',
                        }}
                        linkStyle={{
                            textDecorationLine: 'underline',
                        }}
                        onClickText={onSelect}
                    />
                </Title>
                {error && (
                    <TopNotification
                        level="error"
                        hint={error}
                        containerStyle={{
                            borderBottomWidth: 0,
                            borderTopWidth: 1,
                            marginTop: 8,
                            marginBottom: 0,
                            paddingBottom: 0,
                        }}
                        submitText={Object.R('buttons.writeToSupport')}
                        submitContainerStyle={{
                            borderWidth: 0,
                            alignSelf: 'flex-end',
                            marginRight: 16,
                            marginBottom: 12,
                        }}
                        onSubmit={onSubmitError}
                    />
                )}
                <ButtonsGroup
                    style={{
                        paddingBottom: 12,
                        borderTopWidth: 1,
                        borderTopColor: '#F0F0F0',
                    }}
                >
                    <Button
                        transparent
                        action={onLogout}
                        title="buttons.exit"
                        titleStyles={{
                            fontSize: 14,
                            fontWeight: '900',
                            fontFamily: 'SourceSansPro-Semibold',
                        }}
                    />
                    <Button
                        primary
                        action={onConfirm}
                        title="buttons.continue"
                        disabled={!userAgreementChecked || isLoading}
                        style={{
                            fontSize: 14,
                            fontWeight: '900',
                            fontFamily: 'SourceSansPro-Semibold',
                        }}
                    />
                </ButtonsGroup>
            </View>
        </>
    );
};
